import React, { useEffect, useState } from "react";
import { DashboardLayout } from "@/components/DashboardLayout";
import { StatCard } from "@/components/StatCard";
import { getNav } from "@/lib/navConfig";
import api, { money } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { DollarSign, TrendingUp, Wallet, Package, Percent, FileClock, CheckCircle2, Users, Building2, HardHat, ClipboardList, Target, Loader2, Settings, CreditCard, Repeat, PlayCircle, ClipboardCheck, AlertTriangle, FileWarning } from "lucide-react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [d, setD] = useState(null);
  const [rev, setRev] = useState(null);
  const [goalOpen, setGoalOpen] = useState(false);
  const [goal, setGoal] = useState("");
  const [schedOpen, setSchedOpen] = useState(false);
  const [schedBusy, setSchedBusy] = useState(false);
  const [schedResult, setSchedResult] = useState(null);

  const load = () => api.get("/dashboard/admin").then(({ data }) => { setD(data); setGoal(data.revenue_goal); });
  useEffect(() => { load(); api.get("/reports/revenue").then(({ data }) => setRev(data)).catch(() => {}); }, []);

  const runScheduler = async () => {
    setSchedBusy(true);
    try {
      const { data } = await api.post("/admin/run-recurring-scheduler");
      setSchedResult(data);
      toast.success(`Scheduler ran — ${data.work_orders_created} work order(s) created`);
      load();
    } catch (e) { toast.error("Scheduler run failed"); }
    setSchedBusy(false);
  };

  const saveGoal = async () => { try { await api.put("/settings/goal", { revenue_goal: parseFloat(goal || 0) }); toast.success("Goal updated"); setGoalOpen(false); load(); } catch (e) { toast.error("Failed"); } };

  if (!d) return <DashboardLayout nav={getNav("admin")} title="Executive Dashboard"><div className="flex justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-blue-600" /></div></DashboardLayout>;

  return (
    <DashboardLayout nav={getNav("admin")} title="Executive Dashboard">
      <div className="space-y-8">
        {/* Compliance alerts (Phase 2) */}
        {(d.compliance_awaiting_review > 0 || d.compliance_open_issues > 0 || d.compliance_open_materials > 0 || d.compliance_open_change_orders > 0) && (
          <div className="bg-white border border-amber-200 rounded-2xl p-5" data-testid="compliance-alerts">
            <div className="flex items-center gap-2 text-amber-700 font-semibold text-sm mb-3"><AlertTriangle className="w-4 h-4" /> Needs SJ Attention</div>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
              <AlertCard testid="alert-awaiting-review" label="Awaiting Review" value={d.compliance_awaiting_review} icon={ClipboardCheck} tone="purple" onClick={() => navigate("/app/dispatch/work-orders?status=Ready for SJ Review")} />
              <AlertCard testid="alert-open-materials" label="Material Approvals" value={d.compliance_open_materials} icon={Package} tone="blue" />
              <AlertCard testid="alert-open-change-orders" label="Change Orders" value={d.compliance_open_change_orders} icon={FileWarning} tone="amber" />
              <AlertCard testid="alert-open-issues" label="Open Issues" value={d.compliance_open_issues} icon={AlertTriangle} tone="rose" />
            </div>
          </div>
        )}
        {/* Revenue Goal Tracker */}
        <div className="bg-slate-950 rounded-2xl p-6 md:p-8 text-white relative overflow-hidden">
          <div className="absolute -right-8 -top-8 w-40 h-40 rounded-full bg-blue-600/20 blur-2xl" />
          <div className="relative flex items-start justify-between flex-wrap gap-4">
            <div>
              <div className="flex items-center gap-2 text-blue-400 text-sm font-semibold"><Target className="w-4 h-4" /> Monthly Revenue Goal</div>
              <div className="mt-2 font-head text-4xl md:text-5xl font-semibold">{money(d.current_monthly_revenue)} <span className="text-slate-500 text-2xl">/ {money(d.revenue_goal)}</span></div>
              <div className="mt-1 text-sm text-slate-400">{money(d.remaining_to_goal)} remaining · {d.percentage_completed}% complete</div>
            </div>
            <Dialog open={goalOpen} onOpenChange={setGoalOpen}>
              <DialogTrigger asChild><Button data-testid="edit-goal-btn" variant="outline" className="bg-white/5 border-white/20 text-white hover:bg-white/10"><Settings className="w-4 h-4 mr-1" /> Edit Goal</Button></DialogTrigger>
              <DialogContent>
                <DialogHeader><DialogTitle className="font-head">Set Monthly Revenue Goal</DialogTitle></DialogHeader>
                <div className="space-y-3">
                  <Label>Goal Amount (USD)</Label>
                  <Input data-testid="goal-input" type="number" value={goal} onChange={(e) => setGoal(e.target.value)} />
                  <Button data-testid="goal-save" onClick={saveGoal} className="w-full bg-blue-600 hover:bg-blue-700">Save Goal</Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>
          <div className="relative mt-6 h-3 rounded-full bg-white/10 overflow-hidden">
            <div className="h-full bg-blue-500 rounded-full transition-[width] duration-700" style={{ width: `${d.percentage_completed}%` }} />
          </div>
        </div>

        {/* Financials */}
        <div>
          <h2 className="font-head text-lg font-semibold text-slate-900 mb-4">Financial Performance</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <StatCard testid="a-monthly-rev" label="Monthly Revenue" value={money(d.monthly_revenue)} icon={DollarSign} accent="text-green-600" />
            <StatCard testid="a-job-rev" label="Job Revenue" value={money(d.job_revenue)} icon={TrendingUp} accent="text-blue-600" />
            <StatCard testid="a-contractor-cost" label="Contractor Costs" value={money(d.contractor_costs)} icon={Wallet} accent="text-amber-600" />
            <StatCard testid="a-material-cost" label="Material Costs" value={money(d.material_costs)} icon={Package} accent="text-orange-600" />
            <StatCard testid="a-gross-profit" label="Gross Profit" value={money(d.gross_profit)} icon={TrendingUp} accent="text-green-600" />
            <StatCard testid="a-gross-margin" label="Gross Margin" value={`${d.gross_margin}%`} icon={Percent} accent="text-emerald-600" />
            <StatCard testid="a-outstanding" label="Outstanding Invoices" value={money(d.outstanding_invoices)} icon={FileClock} accent="text-rose-600" />
            <StatCard testid="a-paid" label="Paid Invoices" value={money(d.paid_invoices)} icon={CheckCircle2} accent="text-green-600" />
          </div>
        </div>

        {/* Operations */}
        <div>
          <h2 className="font-head text-lg font-semibold text-slate-900 mb-4">Operations</h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
            <StatCard testid="a-clients" label="Active Clients" value={d.active_clients} icon={Users} accent="text-blue-600" />
            <StatCard testid="a-properties" label="Properties" value={d.properties} icon={Building2} accent="text-purple-600" />
            <StatCard testid="a-contractors" label="Active Contractors" value={d.active_contractors} icon={HardHat} accent="text-indigo-600" />
            <StatCard testid="a-open-wo" label="Open Work Orders" value={d.open_work_orders} icon={ClipboardList} accent="text-amber-600" />
            <StatCard testid="a-completed" label="Completed Jobs" value={d.completed_jobs} icon={CheckCircle2} accent="text-emerald-600" />
            <StatCard testid="a-avg-value" label="Avg Job Value" value={money(d.average_job_value)} icon={DollarSign} accent="text-green-600" />
          </div>
        </div>

        {/* Recurring revenue / MRR */}
        {rev && (
          <div>
            <h2 className="font-head text-lg font-semibold text-slate-900 mb-4">Recurring & Revenue Streams</h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
              <StatCard testid="a-mrr" label="MRR" value={money(rev.mrr)} icon={Repeat} accent="text-blue-600" sub={`${rev.active_subscriptions} subs`} />
              <StatCard testid="a-arr" label="ARR (proj.)" value={money(rev.arr)} icon={TrendingUp} accent="text-indigo-600" />
              <StatCard testid="a-rev-subscription" label="Subscription" value={money(rev.categories.subscription)} icon={CreditCard} accent="text-blue-600" />
              <StatCard testid="a-rev-job" label="Job Revenue" value={money(rev.categories.job)} icon={DollarSign} accent="text-emerald-600" />
              <StatCard testid="a-rev-turnover" label="Turnover" value={money(rev.categories.turnover)} icon={Package} accent="text-purple-600" />
              <StatCard testid="a-rev-recurring" label="Recurring Maint." value={money(rev.categories.recurring)} icon={Repeat} accent="text-teal-600" />
            </div>
          </div>
        )}

        {/* Recurring automation control */}
        <div className="bg-white border border-slate-200 rounded-xl p-6">
          <h3 className="font-head font-semibold text-slate-900 flex items-center gap-2 mb-2"><Repeat className="w-5 h-5 text-blue-600" /> Recurring Maintenance Automation</h3>
          <p className="text-sm text-slate-500 mb-4">Work orders auto-generate on a background schedule (every 30 min). You can force an immediate run — it uses the same duplicate protection, so it will never create duplicates.</p>
          <Dialog open={schedOpen} onOpenChange={setSchedOpen}>
            <DialogTrigger asChild><Button data-testid="run-scheduler-btn" className="bg-slate-900 hover:bg-slate-800"><PlayCircle className="w-4 h-4 mr-1" /> Run Scheduler Now</Button></DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle className="font-head">Run Recurring Scheduler?</DialogTitle></DialogHeader>
              <p className="text-sm text-slate-600">This checks all active recurring plans and generates any due work orders immediately. Paused and cancelled plans are excluded. Duplicates are prevented automatically.</p>
              {schedResult && (
                <div data-testid="scheduler-result" className="mt-2 rounded-lg bg-slate-50 border border-slate-200 p-4 text-sm space-y-1">
                  <div className="flex justify-between"><span className="text-slate-500">Plans checked</span><span className="font-semibold" data-testid="sched-checked">{schedResult.plans_checked}</span></div>
                  <div className="flex justify-between"><span className="text-slate-500">Work orders created</span><span className="font-semibold text-emerald-600" data-testid="sched-created">{schedResult.work_orders_created}</span></div>
                  <div className="flex justify-between"><span className="text-slate-500">Plans skipped</span><span className="font-semibold" data-testid="sched-skipped">{schedResult.plans_skipped}</span></div>
                  <div className="flex justify-between"><span className="text-slate-500">Errors</span><span className={`font-semibold ${schedResult.errors?.length ? "text-rose-600" : ""}`} data-testid="sched-errors">{schedResult.errors?.length || 0}</span></div>
                  <div className="text-xs text-slate-400 pt-1">Ran {new Date(schedResult.ran_at).toLocaleString()}</div>
                </div>
              )}
              <div className="flex gap-2 mt-2">
                <Button data-testid="run-scheduler-confirm" disabled={schedBusy} onClick={runScheduler} className="flex-1 bg-blue-600 hover:bg-blue-700">{schedBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Confirm & Run"}</Button>
                <Button variant="outline" onClick={() => setSchedOpen(false)} className="flex-1">Close</Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>

        {/* Payment settings */}
        <div className="bg-white border border-slate-200 rounded-xl p-6">
          <h3 className="font-head font-semibold text-slate-900 flex items-center gap-2 mb-3"><CreditCard className="w-5 h-5 text-blue-600" /> Payment Integration</h3>
          <div className="flex items-center gap-2 text-sm">
            <span className="w-2 h-2 rounded-full bg-green-500" />
            <span className="text-slate-600">Stripe connected — <strong>Test Mode</strong>. Clients can pay invoices by card. Claim your Stripe account to go live and receive payouts.</span>
          </div>
          <p className="mt-2 text-xs text-slate-400">Test card: 4242 4242 4242 4242 · any future expiry · any CVC.</p>
        </div>
      </div>
    </DashboardLayout>
  );
}

const ALERT_TONE = {
  purple: "border-purple-200 bg-purple-50 text-purple-700", blue: "border-blue-200 bg-blue-50 text-blue-700",
  amber: "border-amber-200 bg-amber-50 text-amber-800", rose: "border-rose-200 bg-rose-50 text-rose-700",
};
function AlertCard({ testid, label, value, icon: Icon, tone, onClick }) {
  return (
    <button data-testid={testid} onClick={onClick} className={`text-left rounded-xl border p-4 transition-colors ${ALERT_TONE[tone]} ${onClick ? "hover:brightness-95 cursor-pointer" : "cursor-default"}`}>
      <div className="flex items-center gap-2 text-xs font-semibold uppercase opacity-80"><Icon className="w-3.5 h-3.5" /> {label}</div>
      <div className="mt-1 font-head text-3xl font-semibold">{value}</div>
    </button>
  );
}
